/**
 * MyApplicationPanel — Customer dashboard "My Application" view.
 *
 * Shows the user's actual submitted application as a readable card grouped
 * by section (Contact, RV Preferences, Tow Vehicle, Trade-In, Features) and
 * exposes two clear actions:
 *
 *   1. Edit application — opens the homepage form pre-filled with the user's
 *      existing answers via /?edit=<session_id>. Submit replaces matches.
 *   2. Redo application — clears any draft and opens a blank form via
 *      /?redo=true. Submit creates fresh matches and replaces the old ones.
 *
 * Either action ends with new matches loaded into "My Matches".
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit3, RefreshCw, FileText, Mail, Phone, MapPin, DollarSign, Truck, Users, Calendar, Wrench, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value, isDark }) => (
  <div>
    <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{label}</p>
    <p className={`text-sm font-semibold mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>{value || <span className="text-gray-400 font-normal italic">— not provided —</span>}</p>
  </div>
);

const Section = ({ icon: Icon, title, children, isDark }) => (
  <div className={`rounded-2xl p-5 ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-amber-200'}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
      <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>{title}</h3>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

const formatBudget = (n) => {
  if (!n) return null;
  if (typeof n === 'number') return `$${n.toLocaleString()}`;
  return n;
};

const MyApplicationPanel = ({ lead, isDark }) => {
  const navigate = useNavigate();
  const [showRedoConfirm, setShowRedoConfirm] = useState(false);

  if (!lead) return null;

  const mc = lead.matching_criteria || {};
  const tow = lead.tow_vehicle || mc.tow_vehicle || {};
  const trade = lead.trade_info || lead.user_responses?.['Trade Info'] || null;

  const handleEdit = () => {
    if (!lead.session_id) {
      toast.error("Couldn't load your application — please refresh");
      return;
    }
    // Clear any stale draft and open the form in EDIT mode
    try { localStorage.removeItem('rvmatch_form_draft_v2'); } catch { /* ignore */ }
    navigate(`/?edit=${lead.session_id}`);
  };

  const handleRedo = () => {
    setShowRedoConfirm(true);
  };

  const confirmRedo = () => {
    try { localStorage.removeItem('rvmatch_form_draft_v2'); } catch { /* ignore */ }
    setShowRedoConfirm(false);
    navigate('/?redo=true');
  };

  const submittedAt = lead.application_submitted_at || lead.created_at;
  const submittedFmt = (() => {
    try { return new Date(submittedAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return submittedAt || ''; }
  })();

  return (
    <div className="space-y-5" data-testid="my-application-panel">
      {/* Action card */}
      <div className={`rounded-2xl p-5 sm:p-6 ${isDark ? 'bg-gradient-to-br from-blue-900/30 via-gray-900 to-gray-900 border border-blue-700/50' : 'bg-gradient-to-br from-blue-50 to-white border border-blue-200'}`} data-testid="application-actions">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <FileText className="text-white" size={22} />
          </div>
          <div className="flex-1">
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Your RV Match Application</h2>
            <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Submitted {submittedFmt}. Need a tweak? <span className="font-semibold">Edit</span> to keep your answers, or <span className="font-semibold">Redo</span> to start fresh.
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              type="button"
              onClick={handleEdit}
              className="flex-1 sm:flex-none bg-blue-500 hover:bg-blue-600 text-white font-bold"
              data-testid="application-edit-btn"
            >
              <Edit3 size={16} className="mr-2" /> Edit
            </Button>
            <Button
              type="button"
              onClick={handleRedo}
              variant="outline"
              className={`flex-1 sm:flex-none font-bold ${isDark ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              data-testid="application-redo-btn"
            >
              <RefreshCw size={16} className="mr-2" /> Redo
            </Button>
          </div>
        </div>
      </div>

      {/* Contact */}
      <Section icon={Mail} title="Contact Information" isDark={isDark}>
        <Field label="Name" value={lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim()} isDark={isDark} />
        <Field label="Email" value={lead.email} isDark={isDark} />
        <Field label="Phone" value={lead.phone} isDark={isDark} />
        <Field label="ZIP Code" value={lead.zipcode} isDark={isDark} />
      </Section>

      {/* RV Preferences */}
      <Section icon={Truck} title="RV Preferences" isDark={isDark}>
        <Field label="RV Type" value={(mc.rv_type || '').replace(/_/g, ' ')} isDark={isDark} />
        <Field label="Length" value={mc.rv_length} isDark={isDark} />
        <Field label="Budget" value={formatBudget(mc.budget_max)} isDark={isDark} />
        <Field label="Sleeps" value={mc.sleep_capacity} isDark={isDark} />
        <Field label="Camping Style" value={mc.camping_style} isDark={isDark} />
        <Field label="Trip Type" value={mc.trip_type} isDark={isDark} />
        <Field label="Has Kids" value={mc.has_kids === true ? 'Yes' : mc.has_kids === false ? 'No' : null} isDark={isDark} />
        <Field label="Has Pets" value={mc.has_pets === true ? 'Yes' : mc.has_pets === false ? 'No' : null} isDark={isDark} />
        <Field label="Off-grid" value={mc.off_grid === true ? 'Yes' : mc.off_grid === false ? 'No' : null} isDark={isDark} />
      </Section>

      {/* Tow Vehicle */}
      {(tow.make || tow.year) && (
        <Section icon={Calendar} title="Tow Vehicle" isDark={isDark}>
          <Field label="Year" value={tow.year} isDark={isDark} />
          <Field label="Make" value={tow.make} isDark={isDark} />
          <Field label="Model" value={tow.model} isDark={isDark} />
          <Field label="Tow Capacity" value={tow.tow_capacity ? `${tow.tow_capacity} lbs` : null} isDark={isDark} />
          <Field label="Payload" value={tow.payload ? `${tow.payload} lbs` : null} isDark={isDark} />
        </Section>
      )}

      {/* Financing & must-haves (from user_responses) */}
      {lead.user_responses && Object.keys(lead.user_responses).length > 0 && (
        <Section icon={DollarSign} title="Financing & Wishlist" isDark={isDark}>
          <Field label="Financing Plan" value={lead.user_responses['Financing Plan']} isDark={isDark} />
          <Field label="Monthly Payment" value={lead.user_responses['Monthly Payment Range']} isDark={isDark} />
          <Field label="Down Payment" value={lead.user_responses['Down Payment']} isDark={isDark} />
          <Field label="Credit" value={lead.user_responses['Credit Profile']} isDark={isDark} />
          <Field label="Pre-Approved" value={lead.user_responses['Pre-Approved']} isDark={isDark} />
          <Field label="Has Trade-In" value={lead.user_responses['Has Trade-In']} isDark={isDark} />
          <Field label="Must-Haves" value={lead.user_responses['Must-Have RV Features']} isDark={isDark} />
          <Field label="Priorities" value={lead.user_responses['Priority Features']} isDark={isDark} />
        </Section>
      )}

      {/* Notes */}
      {lead.additional_notes && (
        <div className={`rounded-2xl p-5 ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-amber-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Wrench size={16} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
            <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Additional Notes</h3>
          </div>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{lead.additional_notes}</p>
        </div>
      )}

      {/* Danger zone — delete account */}
      <DeleteAccountCard sessionId={lead.session_id} email={lead.email} isDark={isDark} />

      {/* Redo application confirmation modal */}
      {showRedoConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowRedoConfirm(false)}
          data-testid="redo-confirm-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${
              isDark ? 'bg-gray-900 border border-gray-700 text-white' : 'bg-white text-gray-900'
            }`}
            data-testid="redo-confirm-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-extrabold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Redo your application?
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  This wipes your current saved answers and generates brand‑new matches from a fresh application. Your existing matches will be replaced. This can't be undone.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
              <Button
                onClick={() => setShowRedoConfirm(false)}
                variant="outline"
                className={`rounded-full ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : ''}`}
                data-testid="redo-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRedo}
                className="bg-[#F2A900] hover:bg-[#E89400] text-black font-semibold rounded-full"
                data-testid="redo-confirm-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Yes, redo it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DeleteAccountCard = ({ sessionId, email, isDark }) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    if (typed.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/account/delete`, { session_id: sessionId, email });
      toast.success(res.data?.message || 'Account deleted');
      // Clear all local artifacts
      try {
        localStorage.removeItem('rvmatch_form_draft_v2');
        localStorage.removeItem('rvmatch_referrer_ref');
      } catch { /* ignore */ }
      // Route home after a brief delay so the toast is visible
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed — please try again');
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-2xl border-2 ${isDark ? 'border-rose-700/40 bg-rose-900/10' : 'border-rose-300 bg-rose-50/50'} p-5`} data-testid="delete-account-card">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-rose-500 shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Danger zone</h3>
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Delete this account and start over fresh. We'll soft-delete your application, matches, and any saved tools immediately. After 30 days, the data is purged permanently. You can apply again with the same email any time.
          </p>
          {!confirmOpen ? (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white font-bold text-sm"
              data-testid="delete-account-open-btn"
            >
              <Trash2 size={14} /> Delete my account
            </button>
          ) : (
            <div className="space-y-3 max-w-md">
              <p className={`text-sm font-semibold ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
                Type <span className="font-mono px-1.5 py-0.5 rounded bg-rose-200 text-rose-900">DELETE</span> below to confirm.
              </p>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="DELETE"
                className={isDark ? 'bg-gray-800 border-gray-700 text-white' : ''}
                data-testid="delete-account-confirm-input"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy || typed.trim().toUpperCase() !== 'DELETE'}
                  className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
                  data-testid="delete-account-confirm-btn"
                >
                  {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                  {busy ? 'Deleting…' : 'Permanently delete'}
                </Button>
                <Button
                  type="button"
                  onClick={() => { setConfirmOpen(false); setTyped(''); }}
                  variant="outline"
                  disabled={busy}
                  data-testid="delete-account-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyApplicationPanel;
